import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';

const BlogCategoriesModule = () => {
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('blog_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      const existing = data || [];
      if (!existing || existing.length === 0) {
        // Seed default categories if none exist
        const defaults = [
          { name: 'News', description: 'Latest news and updates', display_order: 0, is_active: true },
          { name: 'Tips', description: 'Helpful tips and advice', display_order: 1, is_active: true },
          { name: 'Stories', description: 'Customer stories and experiences', display_order: 2, is_active: true },
        ];
        const { data: inserted, error: insertErr } = await supabase.from('blog_categories').insert(defaults).select();
        if (!insertErr) {
          setCategories(inserted || defaults);
          return;
        }
      }

      setCategories(existing);
    } else {
      // If the blog_categories table doesn't exist in Supabase, fall back to local defaults
      console.error('Error fetching categories:', error);
      const msg = (error && error.message) || String(error);
      if (msg.includes("Could not find the table") || msg.includes('does not exist')) {
        const localDefaults = [
          { id: `local-news`, name: 'News', description: 'Latest news and updates', display_order: 0, is_active: true },
          { id: `local-tips`, name: 'Tips', description: 'Helpful tips and advice', display_order: 1, is_active: true },
          { id: `local-stories`, name: 'Stories', description: 'Customer stories and experiences', display_order: 2, is_active: true },
        ];
        setCategories(localDefaults);
        return;
      }
      // otherwise just log the error and leave categories empty
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategory.name ? newCategory.name.trim() : '';
    const description = newCategory.description ? newCategory.description.trim() : '';
    if (!name) {
      alert('Please enter a category name');
      return;
    }

    try {
      // compute safe display order (max existing + 1)
      const maxOrder = (categories || []).reduce((max, c) => Math.max(max, Number(c.display_order) || 0), -1);
      const display_order = maxOrder + 1;

      const { data, error } = await supabase
        .from('blog_categories')
        .insert([{ name, description, display_order, is_active: true }])
        .select();

        if (error) {
          console.error('Create category error:', error);
          const msg = (error && error.message) || String(error);
          if (msg.includes("Could not find the table") || msg.includes('does not exist')) {
            // Create a local fallback category so the dashboard remains usable
            const localCategory = { id: `local-${Date.now()}`, name, description, display_order, is_active: true };
            setCategories(prev => [...(prev || []), localCategory]);
            setNewCategory({ name: '', description: '' });
            setCategoryDialogOpen(false);
            alert('Created local category (database table missing). Please create the table in Supabase to persist changes.');
            return;
          }
          alert('Could not create category: ' + (error.message || JSON.stringify(error)));
          return;
        }

        const inserted = Array.isArray(data) ? data[0] : data;
        // refresh categories and select the new one
        await fetchCategories();
        setNewCategory({ name: '', description: '' });
        setCategoryDialogOpen(false);
    } catch (err) {
      console.error('Unexpected error creating category:', err);
      alert('Unexpected error: ' + (err.message || String(err)));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    if (categoryId.startsWith('local-')) {
      setCategories(prev => prev.filter(c => c.id !== categoryId));
      return;
    }

    // Check if category has posts
    const { data: posts, error: checkError } = await supabase
      .from('blog_posts')
      .select('id')
      .eq('category_id', categoryId)
      .limit(1);

    if (checkError) {
      alert('Error checking category: ' + checkError.message);
      return;
    }

    if (posts && posts.length > 0) {
      alert('Cannot delete category with existing posts. Please move or delete posts first.');
      return;
    }

    const { error } = await supabase
      .from('blog_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      alert('Error deleting category: ' + error.message);
    } else {
      fetchCategories();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">Blog Categories</h2>
          <p className="text-gray-600">Manage blog categories for organizing posts</p>
        </div>
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Category</DialogTitle>
              <DialogDescription>
                Add a new category for organizing blog posts.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="category-name">Category Name</Label>
                <Input
                  id="category-name"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Real Estate Tips"
                />
              </div>
              <div>
                <Label htmlFor="category-description">Description (Optional)</Label>
                <Textarea
                  id="category-description"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this category"
                />
              </div>
              <Button onClick={handleCreateCategory} className="w-full">
                Create Category
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {categories.map((cat) => (
          <div key={cat.id} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg">
            <div>
              <div className="font-medium">{cat.name}</div>
              {cat.description && <div className="text-sm text-gray-500">{cat.description}</div>}
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteCategory(cat.id)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogCategoriesModule;